import { api } from './client';

export type PagesLocale = 'cs' | 'sk' | 'en';

export interface PagesSite {
  id: number;
  locale: PagesLocale;
  host: string;
  previewHost: string | null;
  siteName: string;
  docsBasePath: string;
  promoBasePath: string;
  docsIndexable: boolean;
  promoIndexable: boolean;
  ogImageMediaId: number | null;
  ogImageKey: string | null;
  homeTitle: string | null;
  homeLead: string | null;
  helpdeskEmail: string | null;
  webUrl: string | null;
  gaMeasurementId: string | null;
  status: 'active' | 'inactive';
}

export interface PagesDocNode {
  id: number;
  parentId: number | null;
  slug: string;
  path: string;
  title: string;
  subtitle: string | null;
  metaDescription: string | null;
  position: number;
  status: 'draft' | 'published';
  noindex: boolean;
  translationKey: string | null;
  updatedAt: string;
}

export interface PagesDoc extends PagesDocNode {
  siteId: number;
  bodyHtml: string;
  ogImageKey: string | null;
  publishedAt: string | null;
}

export interface PagesMedia {
  id: number;
  storageKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  url: string;
}

export interface PagesDocPayload {
  siteId?: number;
  parentId?: number | null;
  title?: string;
  slug?: string;
  subtitle?: string | null;
  metaDescription?: string | null;
  bodyHtml?: string;
  status?: 'draft' | 'published';
  noindex?: boolean;
  ogImageMediaId?: number | null;
  translationKey?: string | null;
}

export type PromoStatus = 'draft' | 'published' | 'expired';

export interface PromoListItem {
  id: number;
  siteId: number;
  partnerId: number | null;
  partnerName: string;
  partnerSlug: string;
  partnerLogo: string | null;
  title: string;
  slug: string;
  excerpt: string | null;
  status: PromoStatus;
  publishedAt: string | null;
  expiresAt: string | null;
  filesCount: number;
  coverMediaId: number | null;
  affilwebSync: boolean;
  affilwebStatus: 'none' | 'pending' | 'sent' | 'error';
  affilwebArticleId: string | null;
  affilwebSentAt: string | null;
  affilwebError: string | null;
  updatedAt: string;
}

export interface PromoFile {
  id: number;
  mediaId: number;
  label: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
  position: number;
}

export interface PromoDetail extends PromoListItem {
  bodyHtml: string;
  legacyUrl: string | null;
  coverUrl: string | null;
  files: PromoFile[];
}

export interface PromoPartner {
  id: number;
  name: string;
  slug: string;
  market: string;
  logo: string | null;
}

export interface PromoPayload {
  partnerId?: number;
  title?: string;
  slug?: string;
  excerpt?: string | null;
  bodyHtml?: string;
  status?: 'draft' | 'published';
  publishedAt?: string;
  files?: Array<{ mediaId: number; label: string }>;
  coverMediaId?: number | null;
  affilwebSync?: boolean;
}

export type PagesSpace = 'affilmaster' | 'affilplatform';
let pagesSpace: PagesSpace = 'affilmaster';
export function setPagesSpace(space: PagesSpace): void {
  pagesSpace = space;
}
export function currentPagesSpace(): PagesSpace {
  return pagesSpace;
}
const base = () => `/api/v1/${pagesSpace}/pages`;

export const pagesApi = {
  sites: () => api.get<PagesSite[]>(`${base()}/sites`).then((sites) => { setPagesHosts(sites); return sites; }),
  updateSite: (id: number, body: Partial<PagesSite>) => api.patch<PagesSite>(`${base()}/sites/${id}`, body),
  tree: (siteId: number) => api.get<PagesDocNode[]>(`${base()}/sites/${siteId}/docs`),
  reorder: (siteId: number, ids: number[]) => api.post<void>(`${base()}/sites/${siteId}/reorder`, { ids }),
  doc: (id: number) => api.get<PagesDoc | null>(`${base()}/docs/${id}`),
  create: (body: PagesDocPayload) => api.post<PagesDoc>(`${base()}/docs`, body),
  update: (id: number, body: PagesDocPayload) => api.patch<PagesDoc>(`${base()}/docs/${id}`, body),
  remove: (id: number) => api.del<void>(`${base()}/docs/${id}`),
  translate: (id: number, locale: PagesLocale) => api.post<PagesDoc>(`${base()}/docs/${id}/translate`, { locale }),
  capabilities: () => api.get<{ translation: boolean }>(`${base()}/capabilities`),
  promoList: (q: Record<string, string | number | undefined>) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') params.set(k, String(v));
    return api.get<{ items: PromoListItem[]; total: number }>(`${base()}/promo?${params.toString()}`);
  },
  promoPartners: () => api.get<PromoPartner[]>(`${base()}/promo/partners`),
  promoGet: (id: number) => api.get<PromoDetail>(`${base()}/promo/${id}`),
  promoCreate: (body: PromoPayload) => api.post<PromoDetail>(`${base()}/promo`, body),
  promoUpdate: (id: number, body: PromoPayload) => api.patch<PromoDetail>(`${base()}/promo/${id}`, body),
  promoAffilweb: (id: number) => api.post<PromoDetail>(`${base()}/promo/${id}/affilweb`, {}),
  promoRemove: (id: number) => api.del<void>(`${base()}/promo/${id}`),
  media: (imagesOnly: boolean) => api.get<PagesMedia[]>(`${base()}/media?images=${imagesOnly ? 1 : 0}&limit=80`),
  upload: (fileName: string, contentBase64: string) =>
    api.post<{ id: number; url: string; fileName: string; mimeType: string; size: number }>(`${base()}/media`, { fileName, contentBase64 }),
};

export function promoPreviewUrl(locale: PagesLocale, partnerSlug: string, slug: string): string {
  return pagesPreviewUrl(locale, `/promo-news/${partnerSlug}/${slug}`);
}

export function promoDraftPreviewUrl(locale: PagesLocale, partnerSlug: string, slug: string): string {
  return `${window.location.origin}/nahled/podpora/${locale}/promo-news/${partnerSlug}/${slug}`;
}

const DEFAULT_PAGES_HOSTS: Record<PagesSpace, Record<PagesLocale, string>> = {
  affilmaster: { cs: 'podpora.affilmaster.cz', sk: 'podpora.affilmaster.sk', en: 'support.affilmaster.com' },
  affilplatform: { cs: 'support.affilplatform.com', sk: 'support.affilplatform.com', en: 'support.affilplatform.com' },
};
const pagesHosts: Record<PagesSpace, Record<PagesLocale, string>> = { affilmaster: { ...DEFAULT_PAGES_HOSTS.affilmaster }, affilplatform: { ...DEFAULT_PAGES_HOSTS.affilplatform } };

export function setPagesHosts(sites: PagesSite[]): void {
  for (const s of sites) if (s.host && (s.locale === 'cs' || s.locale === 'sk' || s.locale === 'en')) pagesHosts[pagesSpace][s.locale] = s.host;
}

function isLocalHost(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
}

export function pagesPreviewUrl(locale: PagesLocale, path: string): string {
  const p = path === '/' ? '/' : path;
  if (isLocalHost()) return `${window.location.origin}/nahled/podpora/${locale}${p}`;
  return `https://${pagesHosts[pagesSpace][locale] ?? DEFAULT_PAGES_HOSTS[pagesSpace][locale]}${p}`;
}

const CHUNK_SIZE = 8 * 1024 * 1024;
export const MAX_ATTACHMENT_BYTES = 500 * 1024 * 1024;

export async function uploadFile(file: File, onProgress?: (fraction: number) => void): Promise<{ id: number; url: string; fileName: string; mimeType: string; size: number }> {
  if (file.size <= CHUNK_SIZE) return pagesApi.upload(file.name, await readFileBase64(file));
  const start = await api.post<{ uploadId: string; chunkSize: number }>(`${base()}/media/chunks`, { fileName: file.name, size: file.size });
  const size = start.chunkSize || CHUNK_SIZE;
  let index = 0;
  for (let offset = 0; offset < file.size; offset += size) {
    const part = file.slice(offset, Math.min(offset + size, file.size));
    await api.post(`${base()}/media/chunks/${start.uploadId}`, { index, contentBase64: await readFileBase64(part as File) });
    index += 1;
    onProgress?.(Math.min(offset + size, file.size) / file.size);
  }
  return api.post(`${base()}/media/chunks/${start.uploadId}/finish`, {});
}

export function readFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Soubor se nepodařilo načíst'));
    reader.readAsDataURL(file);
  });
}
